import { useAtomCallback, useReducerAtom } from 'jotai/utils';
import { selectedChatAtom, selectedChatIdAtom } from './../states/selected-chat';
import { useConversationPermissions } from './../../../../shared/hooks/use-permissions';
import {
  AssigneeUpdatesType,
  AssignedSocketUpdates,
  ChatsUpdates,
  ChatsUpdatesTypes,
  CustomChatMetaFields,
  CustomChatMetaSingleSelectField,
  ChatStatuses,
} from './../typings/index';
import { ChatType } from '../components/chat-list-section/chat-row/typings';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import useSocket from '../../../../shared/hooks/use-socket';
import { getOrganizationsSocket } from '../../../../infra/sockets/organization-socket';
import { ChannelAccessUpdate } from '../typings';
import { chatListFamily, chatListSetter, chatRowSetter } from '../states/chat';
import { teamMembersAtom } from '../states/team-members';
import { firebaseAuth, getCurrentUserId } from '../../../../infra/firebase/init';
import { useChatUpdates } from './use-chat-updates';
import { CHAT_UPDATE_TYPE } from '../typings/chat';
import useNotifications from '../../../../shared/hooks/use-notifications';
import ChatCache from '../states/cache';
import { useChatListFilter } from './use-chat-list-filter';
import { wabaIntegrationMembersResetFromUserId } from '../../../../shared/states/waba';
import {
  AssigneeUpdatesPayload,
  ChatFiltersEventPayload,
  ChatFiltersEventReasonTypes,
  CustomFieldUpdatePayload,
  EntityTypes,
} from '../components/chat-list-section/chat-row/typings';
import { socketConnectionAtom, socketConnectionReducer } from '../../../../shared/states/socket-connection';
import { SocketConnectionReducerActionTypes, SocketName } from '../../../../shared/typings/socket-connection';
import {
  customerDetailAtomFamily,
  listFieldChatSetter,
  listFieldSocketUpdateSetter,
  multiListFieldSocketUpdateSetter,
} from '../states/customer-details';
import { updatedCustomFieldEventAtom } from '../states/custom-fields';
import { ContactSupportOutlined } from '@mui/icons-material';
import { CustomerSegmentFilterType } from '../../../customers/typings';
import { chatFilterStatusCountsAtom, FiltersState } from '../states/chat-list';
import { GetChatStatusFilterCount } from '../components/chat-list-section/chat-row/typings';
import { useInternalOrgs } from './use-internal-orgs';

const useChannelAccessSocket = () => {
  const { addNotification } = useNotifications();
  const { updateChats } = useChatUpdates();
  const { getConversationPermissions } = useConversationPermissions();
  const [_, dispatch] = useReducerAtom(socketConnectionAtom, socketConnectionReducer);
  const { isInternal } = useInternalOrgs();
  const { socket: organisationSocket } = useSocket(getOrganizationsSocket, updateChats, false, undefined);
  const setChatList = useSetAtom(chatListSetter);
  const setChatRow = useSetAtom(chatRowSetter);
  const setSelectedChatId = useSetAtom(selectedChatIdAtom);
  const currentUserIdRef = useRef(firebaseAuth.currentUser?.uid);
  const resetIntegrationMembers = useSetAtom(wabaIntegrationMembersResetFromUserId);
  const currentUserId = getCurrentUserId();
  const { assignedUserId: filteredAssignee, isFilterSelectRef } = useChatListFilter();
  const teamMembers = useAtomValue(teamMembersAtom);
  const createChatCache = useAtomCallback(
    useCallback((get, set) => {
      return ChatCache.getInstance(get, set);
    }, [])
  );
  const accessUpdatesListener = useCallback(
    (updates: ChannelAccessUpdate | ChannelAccessUpdate[] | null) => {
      if (!updates) return;
      const parsedUpdates = Array.isArray(updates) ? updates : [updates];
      setChatList({
        type: CHAT_UPDATE_TYPE.ACCESS_UPDATES,
        data: {
          updates: parsedUpdates.filter(({ userId }) => userId === currentUserIdRef.current),
          currentUserId: currentUserIdRef.current,
          addNotification: async (message) => {
            await addNotification({ message });
          },
        },
      });
    },
    [setChatList, addNotification]
  );
  const assigneUpdatesListener = useCallback(
    async (updates: AssignedSocketUpdates, checkForValidUserIds?: boolean) => {
      const { chatId, userId, type, validUserIds, assigneeUpdatedTime } = updates;
      // For UNASSIGNED events, skip the validUserIds gate so all viewers
      // clear the assigned badge, not just the previously assigned agent.
      if (
        checkForValidUserIds &&
        currentUserId &&
        !validUserIds?.includes(currentUserId) &&
        type === AssigneeUpdatesType.ASSIGNED
      ) {
        return;
      }

      let assignedUserId = type === AssigneeUpdatesType.ASSIGNED ? userId : null;
      const permissions = getConversationPermissions(chatId, assignedUserId);
      const skipNew =
        type === AssigneeUpdatesType.ASSIGNED && filteredAssignee !== assignedUserId
          ? true
          : !assignedUserId && filteredAssignee !== 'null'
          ? true
          : false;
      //FIXME PERMISSION CHECK
      if (permissions.canRead) {
        const assignedUserName = assignedUserId
          ? teamMembers.data?.find((m) => m.id === assignedUserId)?.name ?? ''
          : null;
        setChatRow({
          type: CHAT_UPDATE_TYPE.UPDATE,
          partial: true,
          skipNew,
          reOrder: false,
          data: {
            id: chatId,
            assignedUserId,
            assignedUserName,
            assigneeUpdatedTime,
          },
        });
      } else {
        setChatRow({
          type: CHAT_UPDATE_TYPE.DELETE,
          id: chatId,
        });
        setSelectedChatId((id) => {
          if (id === chatId) {
            return '';
          }
          return id;
        });
        const chatCache = await createChatCache();
        chatCache.deleteChat(chatId);
      }
    },
    [
      getConversationPermissions,
      setChatRow,
      filteredAssignee,
      isFilterSelectRef,
      setSelectedChatId,
      createChatCache,
      currentUserId,
      teamMembers,
    ]
  );
  const onChatsUpdatesEventHandler = useCallback(
    (payload: ChatsUpdates) => {
      switch (payload.type) {
        case ChatsUpdatesTypes.DELETED: {
          const { chatIds } = payload;
          chatIds.forEach((chatId) => setChatRow({ type: CHAT_UPDATE_TYPE.DELETE, id: chatId }));
          break;
        }
        default:
          break;
      }
    },
    [setChatRow]
  );
  const assigneBulkUpdatesListener = useCallback(
    (updates: AssignedSocketUpdates[]) => {
      updates.forEach(async (update) => await assigneUpdatesListener(update, true));
    },
    [assigneUpdatesListener]
  );

  const onWabaRoleUpdate = useAtomCallback(
    useCallback(
      (
        get,
        set,
        payload: Array<{
          userId: string;
        }>
      ) => {
        const cache = ChatCache.getInstance();
        resetIntegrationMembers(payload);
        const resetChatList = new CustomEvent('resetChatList');
        document.dispatchEvent(resetChatList);

        const selectedChat = get(selectedChatAtom);
        const integrationId = selectedChat?.integrationId;
        if (!integrationId) return;
        let doneForSelected = false;
        if (Array.isArray(payload)) {
          payload.forEach(({ userId }) => {
            if (doneForSelected) return;
            const integrations = Array.from(cache.getUserToIntegrationCache(userId) ?? []);
            if (!integrations.includes(integrationId)) return;

            const refreshIntegrationMember = new CustomEvent('refreshIntegrationMember', {
              detail: integrationId,
            });
            document.dispatchEvent(refreshIntegrationMember);
            doneForSelected = true;
          });
        }
      },
      [resetIntegrationMembers]
    )
  );

  const removeChat = useCallback(
    (chatId: string, validUserIds?: string[]) => {
      if (currentUserId && !validUserIds?.includes(currentUserId)) return;

      setChatRow({ type: CHAT_UPDATE_TYPE.DELETE, id: chatId });
      // setSelectedChatId((id) => {
      //   if (id === chatId) {
      //     return '';
      //   }
      //   return id;
      // });
    },
    [setChatRow, setSelectedChatId]
  );

  const onChatFiltersAddChatEventHandler = useCallback(
    (payload: ChatFiltersEventPayload) => {
      switch (payload.eventReason) {
        case ChatFiltersEventReasonTypes.ASSIGNEE_UPDATES:
          assigneUpdatesListener(payload.eventPayload, true);
          break;
        case ChatFiltersEventReasonTypes.ASSIGNEE_UPDATES_BULK:
          assigneBulkUpdatesListener(payload.eventPayload);
          break;
        // case ChatFiltersEventReasonTypes.CHAT_UPDATES:
        //   onChatsUpdatesEventHandler(payload.eventPayload);
        //   break;
        default:
          break;
      }
    },
    [
      assigneUpdatesListener,
      // onChatsUpdatesEventHandler,
      assigneBulkUpdatesListener,
    ]
  );

  const onChatFiltersRemoveChatEventHandler = useCallback(
    (payload: ChatFiltersEventPayload) => {
      switch (payload.eventReason) {
        case ChatFiltersEventReasonTypes.ASSIGNEE_UPDATES:
          removeChat(payload.eventPayload.chatId, payload.eventPayload.validUserIds);
          break;
        case ChatFiltersEventReasonTypes.ASSIGNEE_UPDATES_BULK:
          payload.eventPayload.forEach((update) => {
            removeChat(update.chatId, update.validUserIds);
          });
          break;
        // case ChatFiltersEventReasonTypes.CHAT_UPDATES:
        //   payload.eventPayload.chatIds.forEach((chatId) => removeChat(chatId));
        //   break;
        default:
          break;
      }
    },
    [removeChat]
  );

  const getChatData = useAtomCallback(
    useCallback((get, set, chatId: string) => {
      return get(chatListFamily({ id: chatId }));
    }, [])
  );

  const selectedChat = useAtomValue(selectedChatAtom);
  const phone = selectedChat?.chatType === ChatType.INDIVIDUAL ? selectedChat.phoneNumber : '';
  const integrationWabaNumber = selectedChat?.integrationWabaNumber || '';
  const customerDetailAtom = useMemo(() => {
    return customerDetailAtomFamily({
      id: `${integrationWabaNumber}::${phone}`,
    });
  }, [integrationWabaNumber, phone]);
  const setCustomerDetails = useSetAtom(customerDetailAtom);
  const { data: customerDetails } = useAtomValue(customerDetailAtom);
  const setListFieldChat = useSetAtom(listFieldChatSetter);
  const setListFieldSocketUpdate = useSetAtom(listFieldSocketUpdateSetter);
  const setMultiListFieldSocketUpdate = useSetAtom(multiListFieldSocketUpdateSetter);

  const onCustomerCustomFieldUpdate = useCallback(
    (payload: CustomFieldUpdatePayload) => {
      setCustomerDetails((prevState) => {
        if (!prevState.data) {
          return prevState;
        }

        if (prevState.data.customerId !== payload.customerId) {
          return prevState;
        }

        if (payload.entityType === EntityTypes.CHAT && payload?.chatId !== selectedChat?.id) {
          return prevState;
        }

        const oldFields = prevState.data.customFields ?? [];
        let updatedCustomFields: any = [];

        switch (payload.event) {
          case 'CREATED': {
            const exists = oldFields.some((field) => field.id === payload.id);

            if (!exists) {
              updatedCustomFields = [
                ...oldFields,
                {
                  ...payload,
                  // For MULTI_SELECT type, parse the value as an array
                  ...(payload.type === CustomerSegmentFilterType.MULTI_SELECT
                    ? {
                        values: payload.values || [],
                      }
                    : {}),
                },
              ];
            } else {
              updatedCustomFields = oldFields.map((field) =>
                field.id === payload.id
                  ? {
                      ...field,
                      ...payload,
                      // For MULTI_SELECT type, parse the value as an array
                      ...(payload.type === CustomerSegmentFilterType.MULTI_SELECT
                        ? {
                            values: payload.values || [],
                          }
                        : {}),
                    }
                  : field
              );
            }
            break;
          }
          case 'UPDATED': {
            updatedCustomFields = oldFields.map((field) =>
              field.id === payload.id
                ? {
                    ...field,
                    ...payload,
                    customFieldValueId: payload.customFieldValueId,
                    name: field.name,
                    // For MULTI_SELECT type, parse the value as an array
                    ...(payload.type === CustomerSegmentFilterType.MULTI_SELECT
                      ? {
                          values: payload.values || [],
                        }
                      : {}),
                  }
                : field
            );
            if (
              payload.type === CustomerSegmentFilterType.SINGLE_SELECT &&
              payload.customFieldValueId &&
              payload.value
            ) {
              setListFieldSocketUpdate({
                id: payload.id,
                customFieldValueId: payload.customFieldValueId,
                itemName: payload.value,
              });
            } else if (payload.type === CustomerSegmentFilterType.MULTI_SELECT && payload.values?.length) {
              setMultiListFieldSocketUpdate({ id: payload.id, values: payload.values });
            }

            break;
          }
          case 'DELETED': {
            if (payload.type === CustomerSegmentFilterType.SINGLE_SELECT) {
              updatedCustomFields = oldFields.map((field) =>
                field.id === payload.id ? { ...field, value: null, customFieldValueId: null } : field
              );
            } else if (payload.type === CustomerSegmentFilterType.MULTI_SELECT) {
              updatedCustomFields = oldFields.map((field) =>
                field.id === payload.id ? { ...field, values: [], customFieldValueId: null } : field
              );
            } else {
              updatedCustomFields = oldFields.filter((field) => field.id !== payload.id);
            }
            break;
          }
          default:
            updatedCustomFields = oldFields;
            break;
        }
        return {
          ...prevState,
          data: {
            ...prevState.data,
            customFields: updatedCustomFields,
          },
        };
      });

      setListFieldChat({
        listId: payload.id,
        value: payload.value || null,
      });
    },
    [customerDetails]
  );

  const onChatFiltersAddChatEventHandlerForCustomEvents = async (e: Event) => {
    const customEvent = e as CustomEvent<AssigneeUpdatesPayload>;
    const chatId = customEvent.detail?.eventPayload?.chatId;
    const chatListData = await getChatData(chatId);
    const notificationTime = Number(customEvent.detail.eventPayload.notificationTime);
    if (
      chatListData?.assigneeUpdatedTime &&
      notificationTime &&
      Number(chatListData?.assigneeUpdatedTime) > notificationTime
    ) {
      return;
    }
    const eventPayload = customEvent.detail.eventPayload;
    const finalEventPayload = {
      ...eventPayload,
      assigneeUpdatedTime: notificationTime,
    };
    assigneUpdatesListener(finalEventPayload, false);
  };

  const onChatFiltersAddChatEventHandlerForCustomEventsSW = (event: MessageEvent<any>) => {
    if (event.data && event.data.type === 'assignee-update-silent-sw') {
      const payload = event.data.payload;
      onChatFiltersAddChatEventHandlerForCustomEvents(payload);
    }
  };

  const setChatFilterStatusCounts = useSetAtom(chatFilterStatusCountsAtom);

  const onUserChatCountsEvent = useCallback(
    (payload: {
      orgId: string;
      counts: {
        [key: string]: {
          open?: number;
          close?: number;
          awaitingReply?: number;
          unread?: number;
        };
      };
    }) => {
      // Only process updates when team member filter is ALL_CHATS
      // ALL_CHATS is active when assignedUserId is empty string or undefined
      // Reject updates if any specific team member filter is active
      if (filteredAssignee && filteredAssignee !== '') {
        return; // Reject update - team member filter is active
      }

      const { counts } = payload;

      // Map socket payload status keys to internal ChatStatuses enum values
      const statusMap: Record<string, ChatStatuses> = {
        open: ChatStatuses.OPEN,
        close: ChatStatuses.CLOSED,
        awaitingReply: ChatStatuses.WAITING_FOR_RESPONSE,
        unread: ChatStatuses.UNREAD,
      };

      // Map socket payload filter type keys to FiltersState enum values
      const filterTypeMap: Record<string, FiltersState> = {
        ALL_CHATS: FiltersState.ALL_CHATS,
        MY_CHATS: FiltersState.MY_CHATS,
        UNASSIGNED: FiltersState.UNASSIGNED,
        SLA_STATUS: FiltersState.SLA_STATUS,
      };

      // Status labels mapping
      const statusLabels: Record<ChatStatuses, string> = {
        [ChatStatuses.ALL]: 'All',
        [ChatStatuses.OPEN]: 'Open',
        [ChatStatuses.CLOSED]: 'Closed',
        [ChatStatuses.READ]: 'Read',
        [ChatStatuses.UNREAD]: 'Unread',
        [ChatStatuses.WAITING_FOR_RESPONSE]: 'Awaiting reply',
        [ChatStatuses.DONE]: 'Resolved',
        [ChatStatuses.UNRESOLVED]: 'Open',
      };

      // Filter type labels mapping
      const filterTypeLabels: Record<FiltersState, string> = {
        [FiltersState.ALL_CHATS]: 'All chats',
        [FiltersState.MY_CHATS]: 'My chats',
        [FiltersState.UNASSIGNED]: 'Unassigned',
        [FiltersState.SLA_STATUS]: 'SLA breached',
        [FiltersState.STATUS]: 'Status',
        [FiltersState.TAGS]: 'Tags',
        [FiltersState.ASSIGNEE]: 'Team member',
        [FiltersState.WABA]: 'Channels',
        [FiltersState.NONE]: 'None',
      };

      setChatFilterStatusCounts((prev) => {
        const existingCounts = [...(prev?.data ?? [])];
        const updatedCounts: GetChatStatusFilterCount[] = [];

        // Process each filter type from the payload
        Object.entries(counts).forEach(([filterTypeKey, statusCounts]) => {
          const filterType = filterTypeMap[filterTypeKey];
          if (!filterType) return; // Skip unknown filter types

          // Build counts array from payload
          const countsArray: Array<{ type: ChatStatuses; label: string; count?: string }> = [];

          Object.entries(statusCounts).forEach(([statusKey, count]) => {
            const status = statusMap[statusKey];
            // Only add counts that are greater than 0
            if (status && typeof count === 'number' && count > 0) {
              countsArray.push({
                type: status,
                label: statusLabels[status],
                count: count.toString(),
              });
            }
          });

          // Create or update the filter count entry
          updatedCounts.push({
            type: filterType as GetChatStatusFilterCount['type'],
            label: filterTypeLabels[filterType],
            counts: countsArray,
          });
        });

        // Preserve existing filter count entries that aren't in the payload
        existingCounts.forEach((existing) => {
          const isInPayload = Object.keys(counts).some((key) => filterTypeMap[key] === existing.type);
          if (!isInPayload) {
            updatedCounts.push(existing);
          }
        });

        return { ...prev, data: updatedCounts };
      });
    },
    [setChatFilterStatusCounts, filteredAssignee]
  );

  useEffect(() => {
    organisationSocket.on('channel-access-updates-bulk', accessUpdatesListener);
    organisationSocket.on('channel-access-updates', accessUpdatesListener);
    organisationSocket.on('waba-role-updates', onWabaRoleUpdate);
    return () => {
      organisationSocket.off('channel-access-updates-bulk', accessUpdatesListener);
      organisationSocket.off('channel-access-updates', accessUpdatesListener);
      organisationSocket.off('waba-role-updates', onWabaRoleUpdate);
    };
  }, [organisationSocket, accessUpdatesListener, onWabaRoleUpdate]);

  useEffect(() => {
    /**
     * This event handler performs specific action (e.g. DELETE) on particular chats based on payload
     */
    organisationSocket.on('chats-updates', onChatsUpdatesEventHandler);

    /**
     * This event handler adds new Chat row to existing Chat list
     */
    organisationSocket.on('chat-filters-add-chat', onChatFiltersAddChatEventHandler);

    /**
     * This event handler updates assignee to the chat
     */
    organisationSocket.on('assignee-updates', assigneUpdatesListener);

    organisationSocket.on('customer-custom-field-updates', onCustomerCustomFieldUpdate);

    /**
     * This event handler removes existing Chat row from Chat list
     */
    organisationSocket.on('chat-filters-remove-chat', onChatFiltersRemoveChatEventHandler);

    /**
     * This event handler updates chat filter counts dynamically for all filter types
     * Only enabled for internal organizations for this release
     */
    // if (isInternal) {
    //   organisationSocket.on('user-chat-counts', onUserChatCountsEvent);
    // }

    /**
     * This custom event handler updates assignee to the chat from FCM catched in main thread
     */
    document.addEventListener('assignee-update-silent', onChatFiltersAddChatEventHandlerForCustomEvents);

    /**
     * This custom event handler updates assignee to the chat from FCM catched in firebase-sw
     */
    navigator.serviceWorker.addEventListener('message', onChatFiltersAddChatEventHandlerForCustomEventsSW);

    return () => {
      organisationSocket.off('chats-updates', onChatsUpdatesEventHandler);
      organisationSocket.off('chat-filters-add-chat', onChatFiltersAddChatEventHandler);
      organisationSocket.off('assignee-updates', assigneUpdatesListener);
      organisationSocket.off('chat-filters-remove-chat', onChatFiltersRemoveChatEventHandler);
      // if (isInternal) {
      //   organisationSocket.off('user-chat-counts', onUserChatCountsEvent);
      // }

      document.removeEventListener('assignee-update-silent', onChatFiltersAddChatEventHandlerForCustomEvents);

      navigator.serviceWorker.removeEventListener('message', onChatFiltersAddChatEventHandlerForCustomEventsSW);
    };
  }, [
    organisationSocket,
    onChatsUpdatesEventHandler,
    onChatFiltersAddChatEventHandler,
    onChatFiltersRemoveChatEventHandler,
    onCustomerCustomFieldUpdate,
    onUserChatCountsEvent,
    isInternal,
  ]);
};

export default useChannelAccessSocket;
